"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, Check, Download, ShoppingBag, ChefHat, ReceiptText, Heart } from "lucide-react"
import { useRouter } from "next/navigation"
import { useApp } from "@/lib/context"
import { toast } from "sonner"
import { useModalA11y } from "@/lib/use-modal-a11y"
import { downloadOrderPDF } from "@/lib/pdf-generator"
import { feedbackJaEnviado, cancelarPedidoCliente, MOTIVOS_CANCELAMENTO_CLIENTE } from "@/lib/api"
import FeedbackModal from "./feedback-modal"
import DishDetailModal from "./dish-detail-modal"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { cn, formatPrice } from "@/lib/utils"
import { normalizeOrderStatus, ORDER_STATUS_META, ORDER_STEPS } from "@/lib/order-status"
import OrderWaiting from "./order-waiting"

export default function OrderStatusModal() {
  const {
    t,
    showOrderStatus,
    setShowOrderStatus,
    orderStatus,
    fetchOrderStatus,
    completeOrder,
  } = useApp()

  // Atualizar status ao abrir o modal
  // Use a ref to avoid infinite loop: fetchOrderStatus changes every render
  // because its deps (fetchCurrentStatus) change when orderStatus changes.
  const fetchRef = useRef(fetchOrderStatus)
  fetchRef.current = fetchOrderStatus
  useEffect(() => {
    if (showOrderStatus) fetchRef.current()
  }, [showOrderStatus])

  const closeStatus = useCallback(() => setShowOrderStatus(false), [setShowOrderStatus])
  const router = useRouter()
  const dialogRef = useModalA11y<HTMLDivElement>(!!(showOrderStatus && orderStatus), closeStatus)
  const [showFeedback, setShowFeedback] = useState(false)
  const [selectedDish, setSelectedDish] = useState<any>(null)

  // Cancelamento pelo cliente (só enquanto Pendente)
  const [cancelando, setCancelando] = useState(false)
  const [mostrarMotivos, setMostrarMotivos] = useState(false)
  const [motivoEscolhido, setMotivoEscolhido] = useState("")
  const [motivoOutroTexto, setMotivoOutroTexto] = useState("")

  // ===== Animação de mudança de estado =====
  const statusAtual = orderStatus?.status
  const prevStatusRef = useRef<string | undefined>(undefined)
  const [burst, setBurst] = useState(false)
  useEffect(() => {
    if (!statusAtual) return
    if (prevStatusRef.current && prevStatusRef.current !== statusAtual) {
      setBurst(true)
      const t = setTimeout(() => setBurst(false), 1400)
      prevStatusRef.current = statusAtual
      return () => clearTimeout(t)
    }
    prevStatusRef.current = statusAtual
  }, [statusAtual])

  if (!showOrderStatus || !orderStatus) return null

  const fase = normalizeOrderStatus(orderStatus.status)
  const meta = ORDER_STATUS_META[fase]
  const finalizado = fase === "entregue"

  const formatDate = (dateString: string) =>
    new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateString))

  const totalBuffetes = (orderStatus as any).buffetes?.reduce(
    (sum: number, b: any) => sum + b.subtotal,
    0,
  ) ?? 0
  // O payload em tempo real pode chegar sem itens populados — nunca assumir
  const totalItens = (Array.isArray(orderStatus.itens) ? orderStatus.itens : []).reduce(
    (sum, item) => sum + (item?.prato?.preco || 0) * (item?.quantidade || 0),
    0,
  )
  const orderTotal = totalItens + totalBuffetes

  const cancelarPedido = async () => {
    if (!motivoEscolhido) return
    setCancelando(true)
    const resultado = await cancelarPedidoCliente(
      orderStatus._id,
      motivoEscolhido,
      motivoEscolhido === "Outro" ? motivoOutroTexto.trim() : undefined,
    )
    setCancelando(false)
    if (!resultado.ok) {
      toast.error(resultado.message || "Não foi possível cancelar.")
      return
    }
    toast.success("Pedido cancelado.")
    setMostrarMotivos(false)
  }

  const handleDownloadPDF = async () => {
    try {
      await downloadOrderPDF({
        order: orderStatus,
        customerName: orderStatus.nome_cliente,
        customerPhone: orderStatus.telefone || "",
        paymentMethod: "",
      })
    } catch (error) {
      console.error("Erro ao gerar PDF:", error)
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        ref={dialogRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-background/80 backdrop-blur-xl"
        role="dialog"
        aria-modal="true"
        aria-label="O teu Pedido"
      >
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 24, opacity: 0 }}
          transition={{ type: "spring", damping: 28, stiffness: 260 }}
          className="flex h-full w-full flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header glass */}
          <div className="glass sticky top-0 z-10 flex items-center justify-between border-b px-5 py-4">
            <div className="flex items-center gap-3">
              <span
                className="liquid-sheen flex size-9 items-center justify-center rounded-full text-white shadow-md"
                style={{ background: "linear-gradient(120deg, hsl(24 94% 50%), hsl(16 92% 50%))" }}
              >
                <ReceiptText className="size-4" aria-hidden="true" />
              </span>
              <div>
                <h1 className="font-serif text-lg font-light leading-tight">O teu Pedido</h1>
                <p className="text-xs text-muted-foreground">
                  #{orderStatus._id.slice(-8).toUpperCase()} · {formatDate(orderStatus.createdAt)}
                </p>
              </div>
            </div>

            <button
              onClick={closeStatus}
              className="rounded-full p-2 transition-colors hover:bg-muted"
              aria-label={t("close")}
            >
              <X className="size-5" />
            </button>
          </div>

          <div className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto safe-bottom">
            {/* Estado atual + experiência de espera */}
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative px-5 pt-6"
            >
              {/* Burst quando o estado muda */}
              <AnimatePresence>
                {burst && (
                  <>
                    <motion.span
                      key="ring"
                      initial={{ scale: 0.4, opacity: 0.9 }}
                      animate={{ scale: 2.6, opacity: 0 }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className="pointer-events-none absolute left-1/2 top-10 z-10 size-24 -translate-x-1/2 rounded-full border-4 border-primary"
                      aria-hidden="true"
                    />
                    <motion.div
                      key="tag"
                      initial={{ opacity: 0, y: -14, x: "-50%" }}
                      animate={{ opacity: 1, y: -26, x: "-50%" }}
                      exit={{ opacity: 0, y: -40, x: "-50%" }}
                      transition={{ type: "spring", damping: 20 }}
                      className="absolute left-1/2 top-8 z-20 flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-white shadow-lg shadow-primary/30"
                    >
                      <Check className="size-3.5" /> Estado atualizado!
                    </motion.div>
                  </>
                )}
              </AnimatePresence>

              <div
                className={`rounded-3xl border p-6 text-center ${
                  finalizado
                    ? "border-primary/30 bg-primary/5"
                    : fase === "cancelado"
                      ? "border-destructive/30 bg-destructive/5"
                      : "border-border bg-card/60 backdrop-blur"
                }`}
              >
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] ${
                    fase === "cancelado"
                      ? "bg-destructive/10 text-destructive"
                      : finalizado
                        ? "bg-primary text-primary-foreground shadow"
                        : "bg-primary/10 text-primary"
                  }`}
                >
                  {fase === "preparando" && <ChefHat className="size-3.5" aria-hidden="true" />}
                  {fase === "entregue" && <Check className="size-3.5" aria-hidden="true" />}
                  {meta.label}
                </span>

                <h2 className="mt-3 font-serif text-2xl font-light sm:text-3xl">{meta.title}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{meta.description}</p>

                {/* Enquanto esperas... */}
                {fase !== "cancelado" && <OrderWaiting status={orderStatus.status} />}
              </div>
            </motion.section>

            {/* Passo a passo */}
            {fase !== "cancelado" && (
              <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="mt-4 px-5"
              >
                <div className="rounded-3xl border border-border bg-card/60 p-5 backdrop-blur">
                  <div className="relative flex justify-between">
                    <span
                      aria-hidden="true"
                      className="absolute left-5 right-5 top-[13px] h-0.5 rounded bg-border"
                    />
                    <motion.span
                      aria-hidden="true"
                      className="absolute left-5 top-[13px] h-0.5 rounded bg-primary"
                      initial={{ width: 0 }}
                      animate={{
                        width: `${(Math.min(meta.stepIndex, 2) / (ORDER_STEPS.length - 1)) * 100}%`,
                      }}
                      transition={{ duration: 0.8 }}
                    />
                    {ORDER_STEPS.map((step, i) => {
                      const activo = meta.stepIndex === i
                      const completo = meta.stepIndex > i
                      return (
                        <div key={step.key} className="relative z-10 flex w-16 flex-col items-center gap-1.5">
                          <motion.span
                            animate={
                              activo
                                ? { scale: [1, 1.12, 1], boxShadow: ["0 0 0 0 rgba(255,255,255,0)", "0 0 0 8px rgba(234,88,12,0.12)", "0 0 0 0 rgba(255,255,255,0)"] }
                                : {}
                            }
                            transition={{ duration: 1.6, repeat: Infinity }}
                            className={`flex size-7 items-center justify-center rounded-full border-2 text-xs font-bold ${
                              completo
                                ? "border-primary bg-primary text-white"
                                : activo
                                  ? "border-primary bg-primary text-white shadow-lg"
                                  : "border-border bg-card text-muted-foreground"
                            }`}
                          >
                            {completo ? <Check className="size-3.5" /> : i + 1}
                          </motion.span>
                          <span
                            className={`text-center text-[0.65rem] font-medium uppercase tracking-wider ${
                              activo || completo ? "text-foreground" : "text-muted-foreground"
                            }`}
                          >
                            {step.label}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </motion.section>
            )}

            {/* Itens */}
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="mt-4 px-5 pb-6"
            >
              <div className="overflow-hidden rounded-3xl border border-border bg-card/60 backdrop-blur">
                <div className="border-b border-border/70 px-5 py-3.5">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    Itens do pedido
                  </h3>
                </div>

                <div className="divide-y divide-border/50">
                  {(Array.isArray(orderStatus.itens) ? orderStatus.itens : []).map((item: any, index: number) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() =>
                        item?.prato?._id &&
                        setSelectedDish({
                          ...(item.prato as any),
                          _id: item.prato._id,
                          nome: item.prato.nome || "Item",
                          urls: item.prato.urls || [],
                        } as any)
                      }
                      className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-muted/40"
                    >
                      <div className="size-11 shrink-0 overflow-hidden rounded-xl bg-secondary">
                        {item.prato.urls?.[0] && !item.prato.urls[0].includes("placeholder") ? (
                          <img
                            src={item.prato.urls[0]}
                            alt=""
                            className="size-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.visibility = "hidden"
                            }}
                          />
                        ) : (
                          <div className="flex size-full items-center justify-center">
                            <ShoppingBag className="size-4 text-muted-foreground/50" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{item?.prato?.nome || "Item"}</p>
                        <p className="text-xs text-muted-foreground">
                          {item?.quantidade ?? 1} × {formatPrice(item?.prato?.preco || 0)}
                        </p>
                      </div>
                      <span className="text-sm font-semibold">
                        {formatPrice((item?.prato?.preco || 0) * (item?.quantidade || 0))}
                      </span>
                    </button>
                  ))}

                  {(orderStatus as any).buffetes?.map((b: any, i: number) => (
                    <div key={`b-${i}`} className="flex items-center gap-3 px-5 py-3.5">
                      <div
                        className="flex size-11 shrink-0 items-center justify-center rounded-xl text-white"
                        style={{ background: "linear-gradient(120deg, hsl(24 94% 50%), hsl(16 92% 50%))" }}
                      >
                        <ChefHat className="size-5" aria-hidden="true" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">Bufete · {b.buffeteId?.nome || b.nome}</p>
                        <p className="text-xs text-muted-foreground">{b.quantidade} × {formatPrice(b.preco_unitario)}</p>
                      </div>
                      <span className="text-sm font-semibold">{formatPrice(b.subtotal)}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between border-t border-border/70 bg-secondary/40 px-5 py-4">
                  <span className="text-sm font-medium">Total</span>
                  <span className="font-serif text-2xl text-primary">{formatPrice(orderTotal)}</span>
                </div>
              </div>

              {/* Cancelamento pelo cliente — só enquanto Pendente */}
              {fase === "pendente" && !mostrarMotivos && (
                <button
                  onClick={() => setMostrarMotivos(true)}
                  className="mt-4 w-full rounded-full border border-red-200 py-2.5 text-xs font-semibold text-red-500 transition-colors hover:bg-red-50 dark:border-red-900/50 dark:hover:bg-red-950/30"
                >
                  Cancelar pedido
                </button>
              )}

              {fase === "pendente" && mostrarMotivos && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="mt-4 overflow-hidden rounded-2xl border border-red-200/70 bg-red-50/50 p-4 dark:border-red-900/40 dark:bg-red-950/20"
                >
                  <p className="text-xs font-bold uppercase tracking-wider text-red-500">
                    Porque queres cancelar?
                  </p>
                  <div className="mt-2 grid gap-1.5">
                    {[...MOTIVOS_CANCELAMENTO_CLIENTE, "Outro"].map((m) => (
                      <label
                        key={m}
                        className={cn(
                          "flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2 text-sm transition-colors",
                          motivoEscolhido === m
                            ? "border-red-400 bg-red-100/60 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
                            : "border-border hover:border-red-300",
                        )}
                      >
                        <input
                          type="radio"
                          name="motivo-cliente"
                          checked={motivoEscolhido === m}
                          onChange={() => setMotivoEscolhido(m)}
                          className="accent-red-500"
                        />
                        {m}
                      </label>
                    ))}
                    {motivoEscolhido === "Outro" && (
                      <Textarea
                        autoFocus
                        rows={2}
                        value={motivoOutroTexto}
                        onChange={(e) => setMotivoOutroTexto(e.target.value)}
                        placeholder="Descreve o motivo…"
                        className="resize-none"
                      />
                    )}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setMostrarMotivos(false)}
                      className="flex-1 rounded-full"
                    >
                      Voltar
                    </Button>
                    <Button
                      size="sm"
                      disabled={cancelando || !motivoEscolhido || (motivoEscolhido === "Outro" && !motivoOutroTexto.trim())}
                      onClick={cancelarPedido}
                      className="flex-[2] rounded-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {cancelando ? "A cancelar…" : "Confirmar cancelamento"}
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Ações */}
              <div className="mt-4 flex flex-col gap-2.5 pb-2">
                <button
                  onClick={handleDownloadPDF}
                  disabled={!finalizado}
                  title={
                    finalizado
                      ? "Descarregar a fatura do pedido"
                      : "A fatura fica disponível quando o pedido for entregue"
                  }
                  className={`flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold transition-all ${
                    finalizado
                      ? "liquid-sheen text-white shadow-lg hover:scale-[1.01]"
                      : "cursor-not-allowed bg-muted text-muted-foreground"
                  }`}
                  style={
                    finalizado
                      ? { background: "linear-gradient(120deg, hsl(24 94% 50%), hsl(16 92% 50%))" }
                      : undefined
                  }
                >
                  <Download className="size-4" aria-hidden="true" />
                  {finalizado ? "Descarregar Fatura" : "Fatura disponível após entrega"}
                </button>

                {/* Feedback — habilitado quando o pedido foi entregue */}
                {finalizado && (
                  <motion.button
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => setShowFeedback(true)}
                    className="flex w-full items-center justify-center gap-2 rounded-full border border-emerald-400/50 bg-emerald-50/80 py-3.5 text-sm font-semibold text-emerald-700 backdrop-blur transition-all hover:scale-[1.01] dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300"
                  >
                    <Heart className="size-4 fill-current" aria-hidden="true" />
                    {feedbackJaEnviado(orderStatus._id) ? "Obrigado pelo teu feedback!" : "Deixar feedback"}
                  </motion.button>
                )}

                <button
                  onClick={() => completeOrder()}
                  className="w-full rounded-full border border-border bg-card/70 py-3.5 text-sm font-light tracking-wide backdrop-blur transition-colors hover:bg-card"
                >
                  Voltar ao menu
                </button>

                {/* Manter o vínculo com a mesa: novo pedido rápido após entrega */}
                {finalizado && (
                  <button
                    onClick={() => {
                      setShowOrderStatus(false)
                      router?.push?.("/pedido")
                    }}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary/25 transition-transform hover:scale-[1.01]"
                  >
                    Pedir algo mais
                  </button>
                )}
              </div>
            </motion.section>
          </div>
        </motion.div>
      </motion.div>

      {showFeedback && (
        <FeedbackModal
          pedidoId={orderStatus._id}
          onClose={() => setShowFeedback(false)}
        />
      )}

      {/* Detalhes do prato — clicável mesmo depois do pedido enviado */}
      {selectedDish && (
        <DishDetailModal
          dish={selectedDish}
          zClassName="z-[85]"
          onClose={() => setSelectedDish(null)}
        />
      )}
    </AnimatePresence>
  )
}
